/**
 * OfferFilters Component
 * 
 * Filter bar for offers list: assignment_strategy, reward, partner, user_id search.
 * Matches real database schema (no status or date fields in current_offers).
 */

import React, { useState, useEffect } from 'react';
import { Input, Select } from '@/components/common/FormField';
import type { OfferQueryParams, AssignmentStrategy } from '@/app/types/offers';
import { ASSIGNMENT_STRATEGY_OPTIONS } from '@/app/types/offers';
import { rewardService } from '@/app/services/rewardService';
import { partnerService } from '@/app/services/partnerService';
import './OfferFilters.scss';

interface OfferFiltersProps {
  filters: OfferQueryParams;
  onFiltersChange: (filters: OfferQueryParams) => void;
  onSearch: () => void;
  onReset: () => void;
}

export const OfferFilters: React.FC<OfferFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  onReset,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [rewardOptions, setRewardOptions] = useState<{ value: string; label: string }[]>([]);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [showActiveRewards, setShowActiveRewards] = useState(true);
  const [partnerOptions, setPartnerOptions] = useState<{ value: string; label: string }[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [showOnlyActivePartners, setShowOnlyActivePartners] = useState(true);

  // Fetch rewards for dropdown
  useEffect(() => {
    const fetchRewards = async () => {
      try {
        setRewardsLoading(true);
        // Fetch rewards based on active/archived toggle (never DRAFT)
        const response = await rewardService.queryRewards({
          status: showActiveRewards ? 'ACTIVE' : 'ARCHIVED',
          limit: 1000, // Get all rewards
          offset: 0,
        });
        
        const sortedRewards = [...response.rewards].sort((a, b) => {
          const nameA = (a.reward_name || '').toLowerCase();
          const nameB = (b.reward_name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        const options = [
          { value: '', label: 'All Rewards' },
          ...sortedRewards.map((r) => ({
            value: r.reward_id,
            label: r.reward_variant && r.reward_variant !== 'default'
              ? `${r.reward_name} (${r.reward_variant})`
              : r.reward_name,
          })),
        ];
        setRewardOptions(options);
      } catch (error) {
        console.error('Failed to fetch rewards:', error);
        // Fallback to empty list on error
        setRewardOptions([{ value: '', label: 'All Rewards' }]);
      } finally {
        setRewardsLoading(false);
      }
    };

    fetchRewards();
  }, [showActiveRewards]);

  // Fetch partners for dropdown
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setPartnersLoading(true);
        // Fetch partners based on active filter toggle
        const response = await partnerService.queryPartners({
          is_active: showOnlyActivePartners ? true : undefined, // Show only active or all based on toggle
          limit: 1000, // Get all partners
          offset: 0,
        });
        
        // Sort partners by DBA (doing_business_as) alphabetically
        const sortedPartners = [...response.partners].sort((a, b) => {
          const dbaA = (a.doing_business_as || '').toLowerCase();
          const dbaB = (b.doing_business_as || '').toLowerCase();
          return dbaA.localeCompare(dbaB);
        });
        
        const options = [
          { value: '', label: 'All Partners' },
          ...sortedPartners.map((p) => ({
            value: p.partner_product_id.toString(),
            label: `${p.doing_business_as} (${p.partner_product_id})`,
          })),
        ];
        setPartnerOptions(options);
      } catch (error) {
        console.error('Failed to fetch partners:', error);
        // Fallback to empty list on error
        setPartnerOptions([{ value: '', label: 'All Partners' }]);
      } finally {
        setPartnersLoading(false);
      }
    };

    fetchPartners();
  }, [showOnlyActivePartners]);

  const strategyOptions = [
    { value: '', label: 'All Strategies' },
    ...ASSIGNMENT_STRATEGY_OPTIONS,
  ];

  // Generic handler for filter changes (updates local state only, no API call)
  const handleChange = <K extends keyof OfferQueryParams>(
    key: K,
    value: OfferQueryParams[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  // Handle Enter key to trigger search
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const hasActiveFilters = !!(
    filters.user_id ||
    filters.assignment_strategy ||
    filters.reward_id ||
    filters.partner_product_id
  );

  return (
    <div className="offer-filters" onKeyPress={handleKeyPress}>
      {/* Mobile toggle - consistent with other filters */}
      <button
        className="filters-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-expanded={!isCollapsed}
      >
        <span className="toggle-text">
          🔍 Filters
          {hasActiveFilters && <span className="active-indicator">●</span>}
        </span>
        <span className={`toggle-chevron ${isCollapsed ? '' : 'expanded'}`}>▼</span>
      </button>

      <div className={`filters-content ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Primary filters */}
        <div className="filter-row">
          <Input
            label="User ID"
            value={filters.user_id || ''}
            onChange={(val) => handleChange('user_id', val || undefined)}
            placeholder="Search by user ID..."
          />
          <Select
            label="Assignment Strategy"
            value={filters.assignment_strategy || ''}
            onChange={(val) => handleChange('assignment_strategy', (val as AssignmentStrategy) || undefined)}
            options={strategyOptions}
          />
          <div className="reward-filter-group">
            <Select
              label="Reward"
              value={filters.reward_id || ''}
              onChange={(val) => handleChange('reward_id', val || undefined)}
              options={rewardOptions}
              disabled={rewardsLoading}
            />
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showActiveRewards}
                onChange={(e) => setShowActiveRewards(e.target.checked)}
                disabled={rewardsLoading}
              />
              <span>Show Active rewards</span>
            </label>
          </div>
          <div className="partner-filter-group">
            <Select
              label="Partner"
              value={filters.partner_product_id?.toString() || ''}
              onChange={(val) => handleChange('partner_product_id', val ? parseInt(val, 10) : undefined)}
              options={partnerOptions}
              disabled={partnersLoading}
            />
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showOnlyActivePartners}
                onChange={(e) => setShowOnlyActivePartners(e.target.checked)}
                disabled={partnersLoading}
              />
              <span>Show only active partners</span>
            </label>
          </div>
        </div>

        {/* Actions - always visible */}
        <div className="filter-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={onSearch}
          >
            Search
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={onReset}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferFilters;
